import { tryConsumePostApplyAdPending } from "./postApplyAdPending";
import eventEmitter from "../../../../../shared/util/eventEmitter";
import { useViewjobProctorAllocation } from "../../../../proctor/__generated__/ViewjobProctorContext";
import { JsjPostApplyAdTst } from "../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";

import React, { Suspense, useEffect, useState } from "react";
import { Platform } from "react-native";

const PostApplyAdContent = React.lazy(() =>
  import(/* webpackChunkName: "post-apply-ad" */ "./PostApplyAd").then((module) => ({
    default: module.PostApplyAd,
  })),
);

const MIN_HIDDEN_DURATION_MS = 1500;
const TRIGGER_DELAY_MS = 500;

/**
 * Keeps the PostApply JobCard and GraphQL graph out of the initial ViewJob
 * bundle. The full feature is loaded only after an apply-return trigger.
 */
export function LazyPostApplyAd() {
  const allocation = useViewjobProctorAllocation(JsjPostApplyAdTst);
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const [shouldLoad, setShouldLoad] = useState(false);
  const isEnabled =
    allocation.value === JsjPostApplyAdTst.buckets.Active ||
    allocation.value === JsjPostApplyAdTst.buckets.Testingonly;

  useEffect(() => {
    if (!isEnabled || shouldLoad || Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    let hiddenAt: number | null = null;
    let triggerTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const tryLoad = () => {
      if (tryConsumePostApplyAdPending()) {
        setShouldLoad(true);
      }
    };
    const scheduleLoad = () => {
      if (triggerTimeoutId != null) clearTimeout(triggerTimeoutId);
      triggerTimeoutId = setTimeout(tryLoad, TRIGGER_DELAY_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (document.visibilityState === "visible") {
        const elapsed = hiddenAt == null ? Infinity : Date.now() - hiddenAt;
        hiddenAt = null;
        if (elapsed >= MIN_HIDDEN_DURATION_MS) scheduleLoad();
      }
    };
    const onPageshow = (event: PageTransitionEvent) => {
      if (event.persisted) scheduleLoad();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageshow);
    return () => {
      if (triggerTimeoutId != null) clearTimeout(triggerTimeoutId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageshow);
    };
  }, [isEnabled, shouldLoad]);

  useEffect(() => {
    if (!isEnabled || shouldLoad || Platform.OS === "web") {
      return;
    }

    const handler = ({ mobvjtk }: { jobKey: string; mobvjtk: string }) => {
      if (mobvjtk === viewjobOneGraphModel?.mobvjtk) {
        setShouldLoad(true);
      }
    };
    eventEmitter.on("externalApplyReturn", handler);
    return () => eventEmitter.off("externalApplyReturn", handler);
  }, [isEnabled, shouldLoad, viewjobOneGraphModel?.mobvjtk]);

  if (!shouldLoad) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <PostApplyAdContent initiallyTriggered />
    </Suspense>
  );
}
